import React from 'react';

import { useQuery } from '@apollo/react-hooks';
import t from '@jetshop/intl';
import Image from '@jetshop/ui/Image/Image';
import { css } from 'linaria';
import { styled } from 'linaria/react';

import { theme } from '../../Theme';

import intelligentDesignQuery from './IntelligentDesignQuery.gql';

const itemStyles = css`
   h3 {
      margin-bottom: 6px;
   }
   width: auto;
   display: grid;
   margin-bottom: 10px;
`;

const imageStyles = css`
   margin-bottom: 20px;
`;

const Wrapper = styled.div`
   h2 {
      text-align: center;
      margin-bottom: 32px;
   }
   padding-bottom: 64px;
`;

const Items = styled.div`
   display: grid;
   grid-template-columns: minmax(100px, auto);
   grid-auto-flow: column;
   grid-column-gap: 20px;
   grid-template-columns: repeat(var(--item-count), 400px);
   @media (min-width: ${theme.breakpoints.md}) {
      grid-template-columns: repeat(var(--item-count), 350px);
      border: 1px solid var(--test);
   }

   overflow-x: auto;
`;

const cleanProps = item => {
   let output = {};
   for (let property of item.properties) {
      output[property.name] = property.value.value;
   }
   return output;
};

/**
 * Fetches and displays a category as Intelligent Design
 * 
 * @usage Add a 'malldel' to product and call it e. g. 'intelligentDesignCatId'
 * 
 * From ProductPage.js use 
 * @example 
 * const intelligentDesignCatId = product?.customFields?.find(
      c => c.key === 'intelligentDesignCatId',
   )?.stringValue;
 *
 * @param {String} intelligentDesignCatId 
 * @returns {import('react').FunctionComponentElement}
 */
export const IntelligentDesign = ({ categoryId: intelligentDesignCatId }) => {
   const { data, loading, error } = useQuery(intelligentDesignQuery, {
      variables: { categoryId: intelligentDesignCatId },
      // fetchPolicy: 'no-cache',
   });

   if (error) {
      console.error(
         `RelatedProductCard Apollo could not query for categoryId ${intelligentDesignCatId}. Message: ${error.message}`,
         error,
      );
      return <div>Error! {intelligentDesignCatId}</div>;
   }

   const component = data?.category?.data?.items.find(
      i => i.type === 'Intelligent Design',
   );
   const children = component?.children || [];
   const items = children.map(c => cleanProps(c));

   return loading ? (
      <pre>Loading</pre>
   ) : (
      <Wrapper>
         <h2>{t('Intelligent Design')}</h2>
         <Items style={{ '--item-count': items.length || 1 }}>
            {items?.map(item => (
               <Item {...item} />
            ))}
         </Items>
      </Wrapper>
   );
};

const Item = ({ title, text, image }) => {
   return (
      <div className={itemStyles}>
         {image && (
            <Image
               className={imageStyles}
               aspect={'14:15'}
               src={image}
               crop={true}
               gravity={'smart'}
               sizes={[1, 1, 1 / 4]}
            />
         )}
         {title && <h3>{title}</h3>}
         {text && <p>{text}</p>}
      </div>
   );
};

export default IntelligentDesign;
